import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../utils/api';
import { MdCheckCircle, MdBlock, MdChevronRight, MdClose, MdSearch, MdFilterList, MdRefresh } from 'react-icons/md';
import { cn } from '../../lib/utils';
import { ClearFiltersButton, PageSkeleton } from '@euroasiann/shared/ui';

interface IUpgradeRequest {
    _id: string;
    organizationId: { _id: string; name: string };
    currentPlan: string;
    requestedPlan: string;
    requestType: 'UPGRADE' | 'RENEWAL';
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    createdAt: string;
}

export const UpgradeRequestsPage = () => {
    const queryClient = useQueryClient();
    const [selectedRequest, setSelectedRequest] = useState<IUpgradeRequest | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Fetch Requests
    const { data: requests, isLoading, error, refetch } = useQuery({
        queryKey: ['upgrade-requests'],
        queryFn: async () => {
            try {
                const response = await apiFetch('/api/v1/tech/licenses/upgrade-requests');
                if (!response.ok) throw new Error(`Failed to fetch requests: ${response.status}`);
                const result = await response.json();
                if (!result.success) throw new Error(result.error || 'Failed to fetch requests');
                return result.data as IUpgradeRequest[];
            } catch (err) {
                console.error("Fetch error in UpgradeRequestsPage:", err);
                throw err;
            }
        }
    });

    // Approve/Reject Mutation
    const mutation = useMutation({
        mutationFn: async ({ id, status, reason }: { id: string, status: 'APPROVED' | 'REJECTED', reason?: string }) => {
            const response = await apiFetch(`/api/v1/tech/licenses/upgrade-requests/${id}/status`, {
                method: 'PUT',
                body: JSON.stringify({ status, rejectedReason: reason })
            });
            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || 'Failed to update status');
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['upgrade-requests'] });
            queryClient.invalidateQueries({ queryKey: ['tech-licenses'] }); // Refresh licenses list
            alert('Request processed successfully');
        },
        onError: (err: Error) => {
            alert(`Error: ${err.message}`);
        }
    });

    const handleApprove = (id: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        if (confirm('Are you sure you want to approve this request?')) {
            mutation.mutate({ id, status: 'APPROVED' });
            if (selectedRequest?._id === id) setSelectedRequest(null);
        }
    };

    const handleReject = (id: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        const reason = prompt('Enter rejection reason:');
        if (reason) {
            mutation.mutate({ id, status: 'REJECTED', reason });
            if (selectedRequest?._id === id) setSelectedRequest(null);
        } else if (reason !== null) {
            alert('Reason is required for rejection');
        }
    };

    // Filter Logic
    const filteredRequests = useMemo(() => {
        if (!requests) return [];
        let result = requests;

        if (filterStatus !== 'all') {
            result = result.filter(r => r.status === filterStatus);
        }

        if (searchQuery) {
            const lowerQuery = searchQuery.toLowerCase();
            result = result.filter(r =>
                r.organizationId?.name?.toLowerCase().includes(lowerQuery)
            );
        }
        return result;
    }, [requests, filterStatus, searchQuery]);

    if (isLoading) {
        return (
            <div className="w-full space-y-6">
                <PageSkeleton variant="table" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full space-y-6">
                <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-md">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <MdBlock className="h-5 w-5 text-red-500" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error loading requests</h3>
                            <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                                <p>{(error as Error).message}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }


    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-[hsl(var(--foreground))] mb-1">
                        License Requests
                    </h1>
                    <p className="text-[hsl(var(--muted-foreground))]">Review and manage incoming organization requests for plan upgrades or renewals</p>
                </div>
            </div>

            {/* Toolbar */}
            <div className="p-4 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px] relative">
                    <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0088cc]/65" />
                    <input
                        type="text"
                        placeholder="Search organization..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 rounded-lg bg-[hsl(var(--background))] border border-[hsl(var(--border))] focus:ring-2 focus:ring-emerald-500 outline-none"
                    />
                </div>

                <div className="flex items-center gap-2">
                    <MdFilterList className="text-[#0088cc]/65" />
                    <select
                        value={filterStatus}
                        onChange={e => setFilterStatus(e.target.value)}
                        className="px-3 py-2 rounded-lg bg-[hsl(var(--background))] border border-[hsl(var(--border))] outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                        <option value="all">All Status</option>
                        <option value="PENDING">Pending</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                    </select>
                </div>

                <ClearFiltersButton
                    visible={searchQuery.trim() !== '' || filterStatus !== 'all'}
                    onClick={() => {
                        setSearchQuery('');
                        setFilterStatus('all');
                    }}
                    variant="toolbar"
                />

                <button
                    onClick={async () => {
                        setIsRefreshing(true);
                        await refetch();
                        setTimeout(() => setIsRefreshing(false), 500);
                    }}
                    disabled={isRefreshing}
                    className="p-2 text-[hsl(var(--muted-foreground))] hover:bg-[#0088cc]/10 rounded-lg dark:hover:bg-[#0088cc]/18"
                >
                    <MdRefresh className={cn("w-5 h-5", isRefreshing && "animate-spin")} />
                </button>
            </div>

            {/* Table wrapper matching DataTable */}
            <div className="overflow-x-auto rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm relative z-0">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
                            <th className="px-6 py-4 text-left text-xs font-semibold text-[hsl(var(--foreground))] uppercase tracking-wider">Organization</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-[hsl(var(--foreground))] uppercase tracking-wider">Request Type</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-[hsl(var(--foreground))] uppercase tracking-wider">Plan Transition</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-[hsl(var(--foreground))] uppercase tracking-wider">Date</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-[hsl(var(--foreground))] uppercase tracking-wider text-center w-32">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[hsl(var(--border))] bg-[hsl(var(--card))]">
                        {filteredRequests.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="p-16 text-center">
                                    <p className="text-lg font-semibold text-[hsl(var(--muted-foreground))]">No requests found</p>
                                </td>
                            </tr>
                        ) : (
                            filteredRequests.map((req) => (
                                <tr
                                    key={req._id}
                                    onClick={() => setSelectedRequest(req)}
                                    className="hover:bg-[hsl(var(--muted))] transition-colors group cursor-pointer"
                                >
                                    <td className="px-6 py-4 text-sm text-[hsl(var(--foreground))]">
                                        <div>
                                            <div className="font-semibold text-[hsl(var(--foreground))]">{req.organizationId?.name || 'Unknown'}</div>
                                            <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono">ID: {req.organizationId?._id?.substring(0, 8)}...</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-[hsl(var(--foreground))]">
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${req.requestType === 'UPGRADE'
                                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                                            : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300'
                                            }`}>
                                            {req.requestType}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-[hsl(var(--foreground))]">
                                        <div className="flex items-center text-sm">
                                            <span className="text-[hsl(var(--muted-foreground))] capitalize">{req.currentPlan}</span>
                                            <span className="mx-2 text-[hsl(var(--muted-foreground))]">→</span>
                                            <span className="font-bold text-[hsl(var(--foreground))] capitalize">
                                                {req.requestedPlan}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-[hsl(var(--foreground))]">
                                        <span className="text-[hsl(var(--muted-foreground))]">
                                            {new Date(req.createdAt).toLocaleDateString()}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex items-center justify-end pr-2 gap-2">
                                            {req.status === 'PENDING' && (
                                                <>
                                                    <button
                                                        title="Approve"
                                                        onClick={(e) => handleApprove(req._id, e)}
                                                        className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20 transition-colors"
                                                    >
                                                        <MdCheckCircle className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        title="Reject"
                                                        onClick={(e) => handleReject(req._id, e)}
                                                        className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                                                    >
                                                        <MdBlock className="w-5 h-5" />
                                                    </button>
                                                </>
                                            )}
                                            {req.status !== 'PENDING' && (
                                                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border mr-2 ${req.status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/50' : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800/50'}`}>
                                                    {req.status}
                                                </span>
                                            )}
                                            <MdChevronRight className="w-5 h-5 text-[#0088cc]/65 group-hover:text-[hsl(var(--muted-foreground))] dark:group-hover:text-[hsl(var(--foreground))] ml-2 transition-transform group-hover:translate-x-1" />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Slide-over Drawer */}
            {selectedRequest && (
                <div className="fixed inset-0 z-50 overflow-hidden" aria-labelledby="slide-over-title" role="dialog" aria-modal="true">
                    <div
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
                        onClick={() => setSelectedRequest(null)}
                    ></div>

                    <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                        <div className="pointer-events-auto w-screen max-w-md transform transition-transform ease-in-out duration-300">
                            <div className="flex h-full flex-col bg-[hsl(var(--background))] border-l border-[hsl(var(--border))] shadow-2xl">

                                <div className="px-6 py-5 flex items-center justify-between border-b border-[hsl(var(--border))]">
                                    <h2 className="text-xl font-bold text-[hsl(var(--foreground))]" id="slide-over-title">Request Details</h2>
                                    <button
                                        className="p-2 -mr-2 rounded-lg text-[#0088cc]/65 hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors"
                                        onClick={() => setSelectedRequest(null)}
                                    >
                                        <span className="sr-only">Close panel</span>
                                        <MdClose className="h-6 w-6" />
                                    </button>
                                </div>

                                <div className="relative flex-1 px-6 py-6 overflow-y-auto">
                                    <div className="space-y-8">

                                        <div>
                                            <h3 className="text-sm font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-2">Organization</h3>
                                            <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-4 rounded-xl">
                                                <p className="text-lg font-bold text-[hsl(var(--foreground))]">{selectedRequest.organizationId?.name}</p>
                                                <p className="text-sm font-mono text-[hsl(var(--muted-foreground))] mt-1">ID: {selectedRequest.organizationId?._id}</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <h3 className="text-sm font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-2">Type</h3>
                                                <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-bold uppercase ${selectedRequest.requestType === 'UPGRADE'
                                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                                                    : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300'
                                                    }`}>
                                                    {selectedRequest.requestType}
                                                </span>
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-2">Submitted</h3>
                                                <p className="text-sm font-medium text-[hsl(var(--foreground))]">
                                                    {new Date(selectedRequest.createdAt).toLocaleString(undefined, {
                                                        year: 'numeric', month: 'short', day: 'numeric',
                                                        hour: '2-digit', minute: '2-digit'
                                                    })}
                                                </p>
                                            </div>
                                        </div>

                                        <div>
                                            <h3 className="text-sm font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-3">Plan Transition</h3>
                                            <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-5 rounded-xl flex items-center justify-between">
                                                <div className="text-center w-5/12">
                                                    <p className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1">Current</p>
                                                    <p className="font-bold text-lg capitalize text-[hsl(var(--foreground))]">{selectedRequest.currentPlan}</p>
                                                </div>
                                                <div className="flex-shrink-0 w-2/12 flex justify-center">
                                                    <div className="w-8 h-8 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center text-[hsl(var(--muted-foreground))] font-bold">→</div>
                                                </div>
                                                <div className="text-center w-5/12">
                                                    <p className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1">Requested</p>
                                                    <p className="font-bold text-lg capitalize text-indigo-600 dark:text-indigo-400">{selectedRequest.requestedPlan}</p>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                <div className="p-6 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                                    {selectedRequest.status === 'PENDING' ? (
                                        <div className="flex flex-col gap-3">
                                            <button
                                                onClick={() => handleApprove(selectedRequest._id)}
                                                className="w-full inline-flex justify-center items-center px-4 py-3 border border-transparent text-sm font-bold rounded-xl shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors"
                                            >
                                                <MdCheckCircle className="mr-2 h-5 w-5" />
                                                Approve Request
                                            </button>
                                            <button
                                                onClick={() => handleReject(selectedRequest._id)}
                                                className="w-full inline-flex justify-center items-center px-4 py-3 border border-[hsl(var(--border))] text-sm font-bold rounded-xl shadow-sm text-red-600 bg-transparent hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                            >
                                                <MdBlock className="mr-2 h-5 w-5" />
                                                Reject Request
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-xl p-4 text-center">
                                            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2 uppercase tracking-wide font-semibold">Request Status</p>
                                            <div className="flex items-center justify-center gap-2">
                                                {selectedRequest.status === 'APPROVED' ? <MdCheckCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-400" /> : <MdBlock className="w-6 h-6 text-red-600 dark:text-red-400" />}
                                                <span className={`text-lg font-bold ${selectedRequest.status === 'APPROVED' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                                                    {selectedRequest.status}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
